import React, { createContext, useContext, useState } from "react";

const FilterEditContext = createContext();

// Filter edit actions registry - clean approach without if/else
// Common edit handler for filters with original_input
const createInputBasedEditAction = (config) => ({
  edit: ({ filterToEdit, setFilterData, setFilters, setFilterMessage }) => {
    const originalInput = filterToEdit.filterModel.original_input;
    if (!originalInput) {
      throw new Error(
        `No original input found for ${config.filterType} filter`
      );
    }

    const filterData = config.extractData(originalInput);

    setFilterData((prev) => ({ ...prev, ...filterData }));
    setFilters((prev) => ({
      ...prev,
      [config.sectionKey]: { ...prev[config.sectionKey], collapsed: false },
    }));
    setFilterMessage({
      type: "info",
      message: `${config.displayName} loaded for editing. Modify and click 'Apply Filter' to update.`,
    });
  },
});

const FilterEditActions = {
  genomic: createInputBasedEditAction({
    filterType: "genomic",
    displayName: "Genomic range",
    sectionKey: "range",
    extractData: (originalInput) => ({ region: originalInput }),
  }),

  gene_symbol: createInputBasedEditAction({
    filterType: "gene_symbol",
    displayName: "Gene symbols",
    sectionKey: "symbol",
    extractData: (originalInput) => ({ feature: originalInput }),
  }),

  attribute: {
    edit: ({ filterToEdit, setAttributeFilterString, gridRef, setFilterMessage }) => {
      // Check if we have original string input to load
      const originalInput = filterToEdit.filterModel.original_input;
      
      if (originalInput) {
        // Load the original string into the text input
        if (setAttributeFilterString) {
          setAttributeFilterString(originalInput);
        }
        setFilterMessage({
          type: "info",
          message: "Attribute filter loaded for editing. Modify the filter expression and click 'Apply Filter' to update.",
        });
      } else if (!gridRef?.current?.api) {
        setFilterMessage({
          type: "error",
          message: "Grid not available for editing. Please navigate to the Data View to edit attribute filters.",
        });
        return;
      } else {
        try {
          gridRef.current.api.showAdvancedFilterBuilder();
          // Don't show message - the filter builder opening is self-evident
        } catch (error) {
          setFilterMessage({
            type: "error",
            message: "Failed to open filter builder. Please ensure you're in the Data View.",
          });
        }
      }
    },
  },
};

export const FilterEditProvider = ({
  children,
  setFilterData,
  setFilters,
  setFilterMessage,
  gridRef,
  setAttributeFilterString,
}) => {
  const [editingFilter, setEditingFilter] = useState(null);

  const editFilter = async (index, filterToEdit) => {
    try {
      const action = FilterEditActions[filterToEdit.type];
      if (!action) {
        throw new Error(`No edit action for filter type: ${filterToEdit.type}`);
      }

      // Set editing state
      setEditingFilter({ index, filter: filterToEdit });

      // Execute the edit action
      action.edit({
        filterToEdit,
        setFilterData,
        setFilters,
        setFilterMessage,
        gridRef,
        setAttributeFilterString,
      });
    } catch (error) {
      console.error("Error editing filter:", error);
      setFilterMessage({
        type: "error",
        message: `Failed to edit filter: ${error.message}`,
      });
    }
  };

  const clearEditMode = () => {
    setEditingFilter(null);
    // Don't clear filterData here - let the user decide when to clear
  };

  const isEditMode = !!editingFilter;

  return (
    <FilterEditContext.Provider
      value={{
        editingFilter,
        isEditMode,
        editFilter,
        clearEditMode,
      }}
    >
      {children}
    </FilterEditContext.Provider>
  );
};

// Helper function to check if a filter type is editable
export const isFilterEditable = (filterType) => {
  return filterType && FilterEditActions[filterType];
};

export const useFilterEdit = () => {
  const context = useContext(FilterEditContext);
  if (!context) {
    throw new Error("useFilterEdit must be used within FilterEditProvider");
  }
  return context;
};
